import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, School, Users, Calendar, BookOpen, Image, Award,
  Settings, LogOut, Search, Command, FileText, Bell, ChevronRight, Menu, X, BarChart3, CalendarDays, ClipboardCheck, Mic, UserPlus, Users2, PanelBottom, Paintbrush, Layout, UsersRound, ClipboardList, Scale, UserCircle, Megaphone, Trophy, Globe, Activity, FileDown,
} from "lucide-react";
import { Logo } from "@/components/Brand";
import { useAuth } from "@/lib/auth";
import { useCms } from "@/lib/cms";
import { userCan } from "@/lib/rbac";
import { useEffect, useMemo, useRef, useState } from "react";

const nav = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, section: "Main", perm: "dashboard:view" },
  { to: "/admin/schools", label: "Schools", icon: School, section: "Manage", perm: "schools:read" },
  { to: "/admin/teachers", label: "Teachers", icon: Users, section: "Manage", perm: "teachers:read" },
  { to: "/admin/bookings", label: "Bookings", icon: Calendar, section: "Manage", perm: "bookings:read" },
  { to: "/admin/registrations", label: "Teacher Registration", icon: UserPlus, section: "Manage", perm: "bookings:read" },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays, section: "Manage", perm: "bookings:read" },
  { to: "/admin/programs", label: "Programs", icon: BookOpen, section: "Manage", perm: "programs:read" },
  { to: "/admin/certificates", label: "Certificates", icon: Award, section: "Manage", perm: "certificates:read" },
  { to: "/admin/certificate-designer", label: "Certificate Designer", icon: Paintbrush, section: "Manage", perm: "certificates:write" },
  { to: "/admin/feedback", label: "Training Feedback", icon: ClipboardCheck, section: "Manage", perm: "certificates:read" },
  { to: "/admin/gallery", label: "Gallery", icon: Image, section: "Content", perm: "gallery:read" },
  { to: "/admin/achievements", label: "Achievements", icon: Trophy, section: "Content", perm: "cms:read" },
  { to: "/admin/prospectus-widget", label: "Floating Prospectus", icon: FileDown, section: "Content", perm: "cms:read" },
  { to: "/admin/visitor-counter-widget", label: "Visitor Counter", icon: Activity, section: "Content", perm: "cms:read" },
  { to: "/admin/content", label: "Website content", icon: FileText, section: "Content", perm: "cms:read" },
  { to: "/admin/footer", label: "Footer Management", icon: PanelBottom, section: "Content", perm: "cms:read" },
  { to: "/admin/podcast", label: "Podcast", icon: Mic, section: "Content", perm: "podcast:read" },
  { to: "/admin/podcast-bookings", label: "Podcast Bookings", icon: UserPlus, section: "Content", perm: "podcast:read" },
  { to: "/admin/experts", label: "Skilled Teachers", icon: Users2, section: "Content", perm: "experts:read" },
  { to: "/admin/header-builder", label: "Header Builder", icon: Layout, section: "Content", perm: "cms:read" },
  { to: "/admin/news-ticker", label: "News Ticker", icon: Megaphone, section: "Content", perm: "cms:read" },
  { to: "/admin/legal", label: "Legal Pages", icon: Scale, section: "Content", perm: "cms:read" },
  { to: "/admin/website-metadata", label: "Website Metadata", icon: Globe, section: "Content", perm: "settings:manage" },
  { to: "/admin/users", label: "User Management", icon: UsersRound, section: "Administration", perm: "users:read" },
  { to: "/admin/audit-log", label: "Audit Log", icon: ClipboardList, section: "Administration", perm: "users:read" },
  { to: "/admin/profile", label: "My Profile", icon: UserCircle, section: "Administration", perm: "dashboard:view" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, section: "Insights", perm: "reports:view" },
  { to: "/admin/visitor-analytics", label: "Visitor Analytics", icon: Activity, section: "Insights", perm: "reports:view" },
  { to: "/admin/settings", label: "Settings", icon: Settings, section: "System", perm: "settings:manage" },
];

const grouped = nav.reduce((acc, item) => {
  (acc[item.section] = acc[item.section] || []).push(item);
  return acc;
}, {});

// Placeholder data — replace with `api.get("/notifications")` once a backend endpoint exists.
const PLACEHOLDER_NOTIFICATIONS = [
  { id: "1", title: "New booking request", message: "Delhi Public School requested a training on Aug 12.", time: "5m ago", read: false },
  { id: "2", title: "Certificate issued", message: "12 certificates were emailed to St. Mary's teachers.", time: "1h ago", read: false },
  { id: "3", title: "Training feedback submitted", message: "3 new feedback forms are awaiting review.", time: "3h ago", read: true },
];

function filterNav(user) {
  return nav.filter((n) => !n.perm || userCan(user, n.perm));
}

function filterGrouped(user) {
  const filtered = filterNav(user);
  return filtered.reduce((acc, item) => {
    (acc[item.section] = acc[item.section] || []).push(item);
    return acc;
  }, {});
}

const SidebarBody = ({ user, logout, onClose, navigateTo }) => {
  const { site } = useCms();
  const name = site?.company_name || "";
  const logoW = site?.logo_width ?? 32;
  const logoH = site?.logo_height ?? 32;
  const logoRadius = site?.logo_radius ?? 8;
  const grouped_ = useMemo(() => filterGrouped(user), [user]);
  const filteredNav = useMemo(() => filterNav(user), [user]);
  return (
  <>
    <div className="h-16 px-5 flex items-center border-b border-white/5">
      <div className="flex items-center gap-2 flex-1">
        {site?.logo_url ? (
          <img
            src={site.logo_url.startsWith("http") ? site.logo_url : `${process.env.REACT_APP_BACKEND_URL}${site.logo_url}`}
            alt={name}
            style={{ width: logoW, height: logoH, borderRadius: logoRadius, objectFit: "cover" }}
          />
        ) : (
          <div className="bg-gradient-to-br from-indigo-500 to-emerald-500 grid place-items-center relative overflow-hidden shrink-0" style={{ width: logoW, height: logoH, borderRadius: logoRadius }}>
            <div className="absolute inset-0 bg-[#0F172A]/20" />
            <span className="relative text-white font-display font-bold text-sm">{name.charAt(0).toUpperCase()}</span>
          </div>
        )}
        <div className="flex items-baseline gap-1">
          <span className="font-display text-lg font-semibold tracking-tight text-white">{name}</span>
          {site?.tagline_show && site?.tagline && (
            <span className="text-[10px] text-white/40 uppercase tracking-wider">{site.tagline}</span>
          )}
        </div>
      </div>
      {onClose && (
        <button onClick={onClose} className="text-white/60 hover:text-white lg:hidden" aria-label="Close sidebar">
          <X className="w-5 h-5" />
        </button>
      )}
    </div>

    <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto">
      {Object.entries(grouped_).map(([section, items]) => (
        <div key={section}>
          <div className="px-3 mb-1 text-[10px] uppercase tracking-[0.15em] font-semibold text-white/30">{section}</div>
          <div className="space-y-0.5">
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={onClose}
                data-testid={`sidebar-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all relative ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-white/60 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.div
                        layoutId="sidebar-active-pill"
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-gradient-to-b from-indigo-400 to-emerald-400"
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                      />
                    )}
                    <n.icon className={`w-4 h-4 shrink-0 ${isActive ? "text-white" : "text-white/50 group-hover:text-white/80"}`} strokeWidth={2} />
                    <span className="flex-1">{n.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>

    <div className="p-3 border-t border-white/5">
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-emerald-500 text-white text-xs font-semibold grid place-items-center shrink-0">
          {(user?.name || "A").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate">{user?.name}</div>
          <div className="text-xs text-white/50 truncate">{user?.email}</div>
        </div>
        <button
          onClick={async () => { await logout(); navigateTo("/"); }}
          className="text-white/40 hover:text-white transition-colors"
          data-testid="logout-btn"
          aria-label="Logout"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </div>
  </>
  );
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav_ = useNavigate();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
      if (e.key === "Escape") {
        setCmdOpen(false);
        setNotifOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!notifOpen) return;
    const onClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [notifOpen]);

  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="dashboard-layout">
      {/* Desktop sidebar */}
      <aside className="w-64 hidden lg:flex flex-col bg-[#0F172A] text-white shrink-0 h-screen sticky top-0" data-testid="sidebar-dark">
        <SidebarBody user={user} logout={logout} navigateTo={nav_} />
      </aside>

      {/* Mobile sidebar drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-sidebar">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <motion.aside
            initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
            transition={{ type: "spring", damping: 25, stiffness: 250 }}
            className="relative w-64 h-full flex flex-col bg-[#0F172A] text-white"
          >
            <SidebarBody user={user} logout={logout} navigateTo={nav_} onClose={() => setMobileOpen(false)} />
          </motion.aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-gray-200 bg-white/85 backdrop-blur-lg sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6 lg:px-8" data-testid="dashboard-header">
          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900"
              aria-label="Open menu"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCmdOpen(true)}
              data-testid="cmdk-trigger"
              className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-500 hover:border-gray-300 hover:bg-gray-50 min-w-[280px] transition-all"
            >
              <Search className="w-4 h-4" />
              <span>Search schools, teachers, certificates…</span>
              <kbd className="ml-auto text-[10px] px-1.5 py-0.5 bg-gray-100 rounded font-mono text-gray-600">⌘K</kbd>
            </button>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:inline-flex h-9 items-center gap-2 px-3 rounded-full bg-gray-100 text-xs font-medium text-gray-700" data-testid="header-date">
              <Calendar className="w-3.5 h-3.5 text-gray-500" />
              {today}
            </div>
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen((v) => !v)}
                className="relative h-9 w-9 grid place-items-center rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#3B68E7] focus-visible:ring-offset-2"
                aria-label="Notifications"
                aria-haspopup="true"
                aria-expanded={notifOpen}
                data-testid="header-notifications"
              >
                <Bell className="w-4 h-4 text-gray-600" />
                <span className="absolute top-1.5 right-2 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" />
              </button>
              {notifOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96, y: -8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-40"
                  data-testid="notifications-dropdown"
                >
                  <div className="px-4 py-3 border-b border-gray-100 text-sm font-medium text-gray-900">Notifications</div>
                  <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                    {PLACEHOLDER_NOTIFICATIONS.map((n) => (
                      <li key={n.id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start gap-2">
                          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${n.read ? "bg-gray-300" : "bg-emerald-500"}`} />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{n.title}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{n.message}</div>
                            <div className="text-[11px] text-gray-400 mt-1">{n.time}</div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </div>
            <span className="hidden md:inline-flex h-9 items-center gap-1.5 text-xs px-3 bg-emerald-50 text-emerald-700 rounded-full font-medium border border-emerald-100">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
            </span>
          </div>
        </header>
        <main className="flex-1 px-4 py-6 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
          <div className="max-w-[1280px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {cmdOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center pt-24" onClick={() => setCmdOpen(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-lg mx-4 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
              <Command className="w-4 h-4 text-gray-500" />
              <input autoFocus placeholder="Type a command or search…" className="flex-1 outline-none text-sm" data-testid="cmdk-input" />
              <kbd className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded font-mono">ESC</kbd>
            </div>
            <ul className="max-h-80 overflow-auto p-2">
              {filterNav(user).map((n) => (
                <li key={n.to}>
                  <button onClick={() => { nav_(n.to); setCmdOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm">
                    <n.icon className="w-4 h-4 text-gray-500" />
                    <span>Go to {n.label}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400 ml-auto" />
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      )}
    </div>
  );
}
